/** Preload timeline image elements for canvas playback / export. */

const PLACEHOLDER_WIDTH = 480;
const PLACEHOLDER_HEIGHT = 270;

function resolveMediaUrl(url, backendUrl) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${backendUrl}${url}`;
  return `${backendUrl}/${url}`;
}

/** Shared white PNG element — resolved once per page load. */
let placeholderPromise = null;

export function getWhitePlaceholderImage(
  width = PLACEHOLDER_WIDTH,
  height = PLACEHOLDER_HEIGHT,
) {
  if (!placeholderPromise) {
    placeholderPromise = new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, width);
      canvas.height = Math.max(1, height);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(img);
      img.src = canvas.toDataURL('image/png');
    });
  }
  return placeholderPromise;
}

/** @deprecated Use getWhitePlaceholderImage */
export function createWhitePlaceholderImage(width, height) {
  return getWhitePlaceholderImage(width, height);
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

/**
 * @param {object} rendered
 * @param {string} [backendUrl]
 * @returns {Promise<Record<string, HTMLImageElement>>}
 */
export async function preloadLearningImages(rendered, backendUrl = '') {
  const cache = {};
  const placeholder = await getWhitePlaceholderImage();
  const urls = new Set();
  for (const el of rendered?.elements || []) {
    if (el.type === 'image' && el.image_url) urls.add(el.image_url);
  }
  if (!urls.size) return cache;

  const byUrl = {};
  await Promise.all(
    [...urls].map(async (raw) => {
      try {
        const src = resolveMediaUrl(raw, backendUrl);
        byUrl[raw] = await loadImage(src);
      } catch {
        byUrl[raw] = placeholder;
      }
    }),
  );

  for (const el of rendered.elements || []) {
    if (el.type === 'image' && el.image_url && byUrl[el.image_url]) {
      cache[el.id] = byUrl[el.image_url];
      cache[el.image_url] = byUrl[el.image_url];
    }
  }
  return cache;
}
